import React, { useState } from "react";
import { useAgencyData } from "@/hooks/useAgencyData";
import ScrollArea from "@/components/ui/shared/ScrollArea";
import Table from "@/components/ui/shared/table/Table";
import TableHeader from "@/components/ui/shared/table/TableHeader";
import TableBody from "@/components/ui/shared/table/TableBody";
import TableRow from "@/components/ui/shared/table/TableRow";
import TableCell from "@/components/ui/shared/table/TableCell";
import { format } from "date-fns";
import { Plus, ShoppingCart, Loader2, AlertCircle, CheckCircle2 } from "lucide-react";
import { Dialog, DialogContent, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import SubaccountSuccessModal from "./SubaccountSuccessModal";
import {
  validateEmail,
  validatePhone,
  validateAccountName,
  validateLicenseCode,
  applyPhoneMask,
} from "@/lib/validators/subaccount-validators";

interface ValidationState {
  status: "idle" | "validating" | "valid" | "invalid";
  message: string;
}

interface CreatedAccountData {
  account_name: string;
  email: string;
  temporary_password: string;
}

const AgencySubaccounts: React.FC = () => {
  const { accounts, loading, error, refetchAccounts } = useAgencyData();
  const { user } = useAuth();
  const [searchTerm, setSearchTerm] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    license_code: "",
    nome: "",
    email: "",
    phone: "",
  });

  // Estados de validação
  const [validations, setValidations] = useState<{
    email: ValidationState;
    phone: ValidationState;
    nome: ValidationState;
    license_code: ValidationState;
  }>({
    email: { status: "idle", message: "" },
    phone: { status: "idle", message: "" },
    nome: { status: "idle", message: "" },
    license_code: { status: "idle", message: "" },
  });

  // Estados do modal de sucesso
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [createdAccountData, setCreatedAccountData] = useState<CreatedAccountData | null>(null);
  const [emailSent, setEmailSent] = useState(false);
  const [emailError, setEmailError] = useState<string>("");

  const filteredAccounts = accounts.filter((account) => {
    const search = searchTerm.toLowerCase();
    return (
      account.nome.toLowerCase().includes(search) ||
      account.email.toLowerCase().includes(search) ||
      account.phone.toLowerCase().includes(search)
    );
  });

  const handleInputChange = (field: string, value: string) => {
    // Aplicar máscara no telefone
    if (field === "phone") {
      value = applyPhoneMask(value);
    }

    setFormData((prev) => ({ ...prev, [field]: value }));

    // Resetar validação quando usuário começa a digitar
    setValidations((prev) => ({
      ...prev,
      [field]: { status: "idle", message: "" },
    }));
  };

  const validateField = (field: string, value: string) => {
    let result;

    switch (field) {
      case "email":
        result = validateEmail(value);
        break;
      case "phone":
        result = validatePhone(value);
        break;
      case "nome":
        result = validateAccountName(value);
        break;
      case "license_code":
        result = validateLicenseCode(value);
        break;
      default:
        return;
    }

    setValidations((prev) => ({
      ...prev,
      [field]: {
        status: result.isValid ? "valid" : "invalid",
        message: result.message,
      },
    }));
  };

  const handleBlur = (field: string) => {
    const value = formData[field as keyof typeof formData];
    if (value) {
      validateField(field, value);
    }
  };

  const sendWelcomeEmail = async (data: {
    email: string;
    name: string;
    password: string;
    accountName: string;
  }) => {
    try {
      const domain = window.location.hostname === "localhost"
        ? "localhost:5173"
        : window.location.hostname;

      const agencyId = user?.account?.agency_id || user?.agency_id;

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-welcome-email`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          },
          body: JSON.stringify({
            to: data.email,
            name: data.name,
            password: data.password,
            companyName: data.accountName,
            domain: domain,
            agency_id: agencyId,
          }),
        }
      );

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error || "Erro ao enviar email");
      }

      setEmailSent(true);
      setEmailError("");
      toast.success("Email de boas-vindas enviado com sucesso");
    } catch (err: any) {
      console.error("Error sending welcome email:", err);
      setEmailSent(false);
      setEmailError(err.message || "Erro ao enviar email");
      toast.warning("Conta criada, mas o email não foi enviado");
    }
  };

  const handleResendEmail = async () => {
    if (!createdAccountData) return;

    toast.loading("Reenviando email...");
    await sendWelcomeEmail({
      email: createdAccountData.email,
      name: createdAccountData.account_name,
      password: createdAccountData.temporary_password,
      accountName: createdAccountData.account_name,
    });
    toast.dismiss();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validar todos os campos
    const emailValidation = validateEmail(formData.email);
    const phoneValidation = validatePhone(formData.phone);
    const nomeValidation = validateAccountName(formData.nome);
    const licenseValidation = validateLicenseCode(formData.license_code);

    setValidations({
      email: {
        status: emailValidation.isValid ? "valid" : "invalid",
        message: emailValidation.message,
      },
      phone: {
        status: phoneValidation.isValid ? "valid" : "invalid",
        message: phoneValidation.message,
      },
      nome: {
        status: nomeValidation.isValid ? "valid" : "invalid",
        message: nomeValidation.message,
      },
      license_code: {
        status: licenseValidation.isValid ? "valid" : "invalid",
        message: licenseValidation.message,
      },
    });

    // Se alguma validação falhar, não prosseguir
    if (
      !emailValidation.isValid ||
      !phoneValidation.isValid ||
      !nomeValidation.isValid ||
      !licenseValidation.isValid
    ) {
      toast.error("Por favor, corrija os erros no formulário");
      return;
    }

    setSaving(true);
    const toastId = toast.loading("Criando subconta...");

    try {
      if (!supabase) {
        throw new Error("Supabase não está configurado");
      }

      const agencyId = user?.account?.agency_id || user?.agency_id;

      if (!agencyId) {
        throw new Error("Agência não encontrada");
      }

      // Chamar edge function para criar subconta completa
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/create-subaccount-with-owner`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          },
          body: JSON.stringify({
            agency_id: agencyId,
            license_code: formData.license_code,
            nome: formData.nome,
            email: formData.email,
            phone: formData.phone,
          }),
        }
      );

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error || "Erro ao criar subconta");
      }

      toast.dismiss(toastId);
      toast.success("Subconta criada com sucesso!");

      // Armazenar dados da conta criada
      setCreatedAccountData({
        account_name: result.data.account_name,
        email: result.data.email,
        temporary_password: result.data.temporary_password,
      });

      // Fechar dialog de criação
      setDialogOpen(false);

      // Resetar formulário
      setFormData({
        license_code: "",
        nome: "",
        email: "",
        phone: "",
      });
      setValidations({
        email: { status: "idle", message: "" },
        phone: { status: "idle", message: "" },
        nome: { status: "idle", message: "" },
        license_code: { status: "idle", message: "" },
      });

      // Atualizar lista de contas
      await refetchAccounts();

      // Mostrar modal de sucesso
      setShowSuccessModal(true);

      // Enviar email de boas-vindas em background
      sendWelcomeEmail({
        email: result.data.email,
        name: result.data.account_name,
        password: result.data.temporary_password,
        accountName: result.data.account_name,
      });
    } catch (err: any) {
      toast.dismiss(toastId);
      toast.error(err.message || "Erro ao criar conta");
      console.error("Error creating subaccount:", err);
    } finally {
      setSaving(false);
    }
  };

  const handleCloseSuccessModal = () => {
    setShowSuccessModal(false);
    setCreatedAccountData(null);
    setEmailSent(false);
    setEmailError("");
  };

  const getValidationIcon = (status: ValidationState["status"]) => {
    if (status === "validating") {
      return <Loader2 className="w-4 h-4 text-neutral-400 animate-spin" />;
    }
    if (status === "valid") {
      return <CheckCircle2 className="w-4 h-4 text-green-500" />;
    }
    if (status === "invalid") {
      return <AlertCircle className="w-4 h-4 text-red-500" />;
    }
    return null;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center gap-4">
          <div className="w-8 h-8 border-4 border-neutral-600 border-t-neutral-50 rounded-full animate-spin"></div>
          <p className="text-neutral-50">Carregando subcontas...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.464 0L4.35 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-neutral-50 mb-2">Erro ao carregar subcontas</h3>
          <p className="text-neutral-300 text-sm">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="w-full h-full flex flex-col">
        <div className="w-full mt-3 sm:mt-4 flex-wrap gap-3 flex justify-between items-center mb-4">
          <div className="flex items-center gap-2">
            <Input
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Pesquisar subcontas..."
              className="w-[170px] sm:w-[220px] md:w-[270px] xl:w-[417px] min-h-[40px] rounded-[6px] placeholder:text-neutral-400 text-white border border-neutral-500 bg-neutral-600 px-3"
            />
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button className="border min-h-[40px] text-neutral-50 font-medium text-[14px] px-3 border-neutral-500 bg-neutral-600 rounded-[6px] flex justify-center items-center gap-2 hover:bg-neutral-700 transition-colors">
              <ShoppingCart className="w-4 h-4" />
              Comprar Licença
            </button>
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <button className="bg-[#E4E4E7] min-h-[40px] text-black font-medium text-[14px] px-3 rounded-[6px] flex justify-center items-center gap-2 hover:bg-[#D4D4D8] transition-colors">
                  <Plus className="w-4 h-4" />
                  Adicionar novo
                </button>
              </DialogTrigger>
              <DialogContent className="flex w-full max-w-md p-0 border-[#3F3F464D] bg-[#18181B] flex-col">
                <ScrollArea classNames={{ root: "max-h-[90dvh] p-5 overflow-auto" }}>
                  <h3 className="text-xl font-semibold text-neutral-50 mb-6">
                    Adicionar Nova Conta
                  </h3>
                  <form onSubmit={handleSubmit} className="w-full gap-4 flex flex-col">
                    <div className="flex flex-col w-full items-start gap-3">
                      <Label htmlFor="license_code" className="text-white">
                        Código da Licença
                      </Label>
                      <div className="relative w-full">
                        <Input
                          id="license_code"
                          value={formData.license_code}
                          onChange={(e) => handleInputChange("license_code", e.target.value)}
                          onBlur={() => handleBlur("license_code")}
                          placeholder="Código da licença"
                          className="bg-neutral-600 text-white placeholder:text-[#71717A] min-h-[40px] border border-[#3F3F46] pr-10"
                          required
                          disabled={saving}
                        />
                        <div className="absolute right-3 top-1/2 -translate-y-1/2">
                          {getValidationIcon(validations.license_code.status)}
                        </div>
                      </div>
                      {validations.license_code.status === "invalid" && (
                        <p className="text-red-400 text-sm">{validations.license_code.message}</p>
                      )}
                    </div>

                    <div className="flex flex-col w-full items-start gap-3">
                      <Label htmlFor="nome" className="text-white">
                        Nome do Cliente
                      </Label>
                      <div className="relative w-full">
                        <Input
                          id="nome"
                          value={formData.nome}
                          onChange={(e) => handleInputChange("nome", e.target.value)}
                          onBlur={() => handleBlur("nome")}
                          placeholder="Nome completo"
                          className="bg-neutral-600 text-white placeholder:text-[#71717A] min-h-[40px] border border-[#3F3F46] pr-10"
                          required
                          disabled={saving}
                        />
                        <div className="absolute right-3 top-1/2 -translate-y-1/2">
                          {getValidationIcon(validations.nome.status)}
                        </div>
                      </div>
                      {validations.nome.status === "invalid" && (
                        <p className="text-red-400 text-sm">{validations.nome.message}</p>
                      )}
                    </div>

                    <div className="flex flex-col w-full items-start gap-3">
                      <Label htmlFor="email" className="text-white">
                        E-mail
                      </Label>
                      <div className="relative w-full">
                        <Input
                          id="email"
                          type="email"
                          value={formData.email}
                          onChange={(e) => handleInputChange("email", e.target.value)}
                          onBlur={() => handleBlur("email")}
                          placeholder="email@exemplo.com"
                          className="bg-neutral-600 text-white placeholder:text-[#71717A] min-h-[40px] border border-[#3F3F46] pr-10"
                          required
                          disabled={saving}
                        />
                        <div className="absolute right-3 top-1/2 -translate-y-1/2">
                          {getValidationIcon(validations.email.status)}
                        </div>
                      </div>
                      {validations.email.status === "invalid" && (
                        <p className="text-red-400 text-sm">{validations.email.message}</p>
                      )}
                    </div>

                    <div className="flex flex-col w-full items-start gap-3">
                      <Label htmlFor="phone" className="text-white">
                        Telefone
                      </Label>
                      <div className="relative w-full">
                        <Input
                          id="phone"
                          value={formData.phone}
                          onChange={(e) => handleInputChange("phone", e.target.value)}
                          onBlur={() => handleBlur("phone")}
                          placeholder="(00) 00000-0000"
                          className="bg-neutral-600 text-white placeholder:text-[#71717A] min-h-[40px] border border-[#3F3F46] pr-10"
                          required
                          disabled={saving}
                        />
                        <div className="absolute right-3 top-1/2 -translate-y-1/2">
                          {getValidationIcon(validations.phone.status)}
                        </div>
                      </div>
                      {validations.phone.status === "invalid" && (
                        <p className="text-red-400 text-sm">{validations.phone.message}</p>
                      )}
                    </div>

                    <div className="w-full flex justify-end gap-3 mt-2">
                      <Button
                        type="button"
                        onClick={() => setDialogOpen(false)}
                        className="border border-neutral-500 bg-neutral-600 text-white hover:bg-neutral-700"
                        disabled={saving}
                      >
                        Cancelar
                      </Button>
                      <Button
                        type="submit"
                        className="bg-[#E4E4E7] text-black hover:bg-[#D4D4D8]"
                        disabled={saving}
                      >
                        {saving ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Criando...
                          </>
                        ) : (
                          "Adicionar"
                        )}
                      </Button>
                    </div>
                  </form>
                </ScrollArea>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {filteredAccounts.length === 0 ? (
          <div className="flex items-center justify-center h-64 border border-[#3F3F464D] rounded-lg bg-neutral-800/50">
            <div className="flex flex-col items-center gap-4 text-center p-6">
              <div className="w-16 h-16 bg-neutral-600 rounded-full flex items-center justify-center">
                <svg className="w-8 h-8 text-neutral-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-neutral-50 mb-2">
                  {searchTerm ? "Nenhuma subconta encontrada" : "Nenhuma subconta cadastrada"}
                </h3>
                <p className="text-neutral-300 text-sm">
                  {searchTerm
                    ? "Tente ajustar sua busca"
                    : "Adicione uma nova conta para começar"}
                </p>
              </div>
            </div>
          </div>
        ) : (
          <ScrollArea
            classNames={{
              root: "w-full flex-1 overflow-x-auto border-[1px] border-[#3F3F464D] rounded-[12px] bg-neutral-800/50",
            }}
          >
            <Table className="min-w-[800px]">
              <TableHeader className="border-b-[1px] border-[#3F3F464D]">
                <TableRow>
                  <TableCell className="text-neutral-50 font-semibold">
                    <p className="py-2">Nome</p>
                  </TableCell>
                  <TableCell className="text-neutral-50 font-semibold">E-mail</TableCell>
                  <TableCell className="text-neutral-50 font-semibold">Telefone</TableCell>
                  <TableCell className="text-neutral-50 font-semibold">Licença</TableCell>
                  <TableCell className="text-neutral-50 font-semibold">Data de Criação</TableCell>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredAccounts.map((account) => (
                  <TableRow key={account.id}>
                    <TableCell>
                      <p className="font-medium text-neutral-50">{account.nome}</p>
                    </TableCell>
                    <TableCell>
                      <p className="text-neutral-300">{account.email}</p>
                    </TableCell>
                    <TableCell>
                      <p className="text-neutral-300">{account.phone}</p>
                    </TableCell>
                    <TableCell>
                      <p className="text-neutral-300 font-mono text-sm">
                        {account.license_id ? account.license_id.slice(0, 8) : "-"}
                      </p>
                    </TableCell>
                    <TableCell>
                      <p className="text-neutral-300">
                        {account.created_at
                          ? format(new Date(account.created_at), "dd/MM/yyyy HH:mm")
                          : "-"}
                      </p>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </ScrollArea>
        )}
      </div>

      {/* Modal de Sucesso */}
      {createdAccountData && (
        <SubaccountSuccessModal
          isOpen={showSuccessModal}
          onClose={handleCloseSuccessModal}
          accountData={createdAccountData}
          emailSent={emailSent}
          emailError={emailError}
          onResendEmail={handleResendEmail}
        />
      )}
    </>
  );
};

export default AgencySubaccounts;
